/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\ExpansionModulesTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import { ChevronLeft, ChevronRight, Keyboard } from 'lucide-react';
import Combobox from '../../../components/ui/Combobox';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import { ExpansionKeyConfig } from '../types';
import { extractExpansionModuleInfo } from '../utils/extractOptions';
import type * as api from '../api';

interface ExpansionModulesTabProps {
  selectedModel: api.DeviceModel;
  expansionKeys: ExpansionKeyConfig[];
  setExpansionKeys: (updater: (prev: ExpansionKeyConfig[]) => ExpansionKeyConfig[]) => void;
  selectedExpModule: number;
  setSelectedExpModule: (n: number) => void;
  expPage: number;
  setExpPage: (page: number | ((p: number) => number)) => void;
  expPerPage: number;
  setExpPerPage: (n: number) => void;
  setValue: (field: 'expansionModules', value: ExpansionKeyConfig[]) => void;
}

export function ExpansionModulesTab({
  selectedModel,
  expansionKeys,
  setExpansionKeys,
  selectedExpModule,
  setSelectedExpModule,
  expPage,
  setExpPage,
  expPerPage,
  setExpPerPage,
  setValue,
}: ExpansionModulesTabProps) {
  const { t } = useI18n();
  const expInfo = extractExpansionModuleInfo(selectedModel.expansionModuleKeys, selectedModel.accounts);

  if (expInfo.moduleCount === 0 || expInfo.keysPerModule === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{t('provisioningTemplates.noExpansionModules', 'This model does not support Expansion Modules configuration.')}</p>
      </div>
    );
  }

  const moduleKeys = expansionKeys.filter(k => k.moduleNumber === selectedExpModule);
  const totalItems = moduleKeys.length;
  const totalPages = Math.ceil(totalItems / expPerPage);
  const startIndex = (expPage - 1) * expPerPage;
  const endIndex = Math.min(startIndex + expPerPage, totalItems);
  const currentPageKeys = moduleKeys.slice(startIndex, endIndex);

  const lineOptions = Array.from({ length: expInfo.maxAccounts }, (_, i) => ({
    value: `Line ${i + 1}`,
    label: `Line ${i + 1}`,
  }));

  const updateExpKey = (keyIndex: number, field: keyof ExpansionKeyConfig, value: string) => {
    const keyToUpdate = currentPageKeys[keyIndex];
    const absoluteIndex = expansionKeys.findIndex(
      k => k.moduleNumber === keyToUpdate.moduleNumber && k.keyNumber === keyToUpdate.keyNumber
    );
    if (absoluteIndex === -1) return;
    setExpansionKeys(prev => {
      const updated = [...prev];
      updated[absoluteIndex] = { ...updated[absoluteIndex], [field]: value };
      setValue('expansionModules', updated);
      return updated;
    });
  };

  return (
    <FormBox title={t('provisioningTemplates.tabs.expansion', 'Key Expansion Module')} icon={<Keyboard className="h-4 w-4" />}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{t('provisioningTemplates.module', 'Module')}:</span>
            <select value={selectedExpModule} onChange={(e) => { setSelectedExpModule(Number(e.target.value)); setExpPage(1); }}
              className="px-3 py-1.5 border border-border rounded text-sm bg-background">
              {Array.from({ length: expInfo.moduleCount }, (_, i) => (
                <option key={i + 1} value={i + 1}>{`${t('provisioningTemplates.module', 'Module')} ${i + 1}`}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <select value={expPerPage} onChange={(e) => { setExpPerPage(Number(e.target.value)); setExpPage(1); }}
              className="px-2 py-1 border border-border rounded text-sm bg-background">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-muted-foreground">{t('common.itemsPerPage', 'entries per page')}</span>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {expInfo.keysPerModule} {t('provisioningTemplates.keysPerModule', 'keys per module')}
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-16">{t('provisioningTemplates.key', 'Key')}</th>
              <th className="px-3 py-2 text-left font-medium w-60">{t('provisioningTemplates.type', 'Type')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('common.description', 'Description')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.value', 'Value')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('provisioningTemplates.extension', 'Extension')}</th>
              <th className="px-3 py-2 text-left font-medium w-40">{t('provisioningTemplates.line', 'Line')}</th>
            </tr>
          </thead>
          <tbody>
            {currentPageKeys.map((key, idx) => (
              <tr key={`${key.moduleNumber}-${key.keyNumber}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-3 py-2 text-muted-foreground font-medium">{key.keyNumber}</td>
                <td className="px-3 py-1">
                  <Combobox single
                    options={(expInfo.types.length > 0 ? expInfo.types : [{ value: 'Line', label: 'Line' }]).map(type => ({
                      value: type.value, label: type.label, checked: key.type === type.value,
                    }))}
                    onToggle={(val) => updateExpKey(idx, 'type', val)}
                    placeholder={t('provisioningTemplates.selectType', 'Select type...')} className="w-full" />
                </td>
                <td className="px-3 py-1">
                  <input type="text" value={key.description} onChange={(e) => updateExpKey(idx, 'description', e.target.value)}
                    className="w-full px-2 py-1 border border-border rounded text-sm bg-background" />
                </td>
                <td className="px-3 py-1">
                  <input type="text" value={key.value} onChange={(e) => updateExpKey(idx, 'value', e.target.value)}
                    className="w-full px-2 py-1 border border-border rounded text-sm bg-background" />
                </td>
                <td className="px-3 py-1">
                  <input type="text" value={key.extension} onChange={(e) => updateExpKey(idx, 'extension', e.target.value)}
                    className="w-full px-2 py-1 border border-border rounded text-sm bg-background" />
                </td>
                <td className="px-3 py-1">
                  <Combobox single
                    options={lineOptions.map(opt => ({ value: opt.value, label: opt.label, checked: key.line === opt.value }))}
                    onToggle={(val) => updateExpKey(idx, 'line', val)}
                    placeholder={t('provisioningTemplates.selectLine', 'Select line...')} className="w-full" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          {t('common.showing', 'Showing')} {startIndex + 1} {t('common.to', 'to')} {endIndex} {t('common.of', 'of')} {totalItems} {t('common.results', 'entries')}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setExpPage(1)} disabled={expPage === 1}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
          <button type="button" onClick={() => setExpPage(p => Math.max(1, p - 1))} disabled={expPage === 1}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) pageNum = i + 1;
            else if (expPage <= 3) pageNum = i + 1;
            else if (expPage >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = expPage - 2 + i;
            return (
              <button key={pageNum} type="button" onClick={() => setExpPage(pageNum)}
                className={`px-3 py-1 text-sm border border-border rounded ${expPage === pageNum ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                {pageNum}
              </button>
            );
          })}
          <button type="button" onClick={() => setExpPage(p => Math.min(totalPages, p + 1))} disabled={expPage === totalPages}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setExpPage(totalPages)} disabled={expPage === totalPages}
            className="px-2 py-1 text-sm border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed">�</button>
        </div>
      </div>
    </FormBox>
  );
}
